import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'detrix.sessionViewer.workspaceWeights.v1';
const SUM = 3;
const MIN = 0.55; // ~18.3% each when sum=3
const DEFAULT: [number, number, number] = [1, 1, 1];

function readStored(): [number, number, number] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as unknown;
    if (!Array.isArray(a) || a.length !== 3) return null;
    const w = a.map(Number) as [number, number, number];
    if (!w.every((x) => Number.isFinite(x) && x > 0)) return null;
    const s = w[0]! + w[1]! + w[2]!;
    if (s <= 0) return null;
    const n = (SUM / s) as number;
    return [w[0]! * n, w[1]! * n, w[2]! * n];
  } catch {
    return null;
  }
}

function clampWeights(w1: number, w2: number, w3: number): [number, number, number] {
  const a = Math.max(MIN, w1);
  const b = Math.max(MIN, w2);
  const c = Math.max(MIN, w3);
  const s = a + b + c;
  const scale = SUM / s;
  return [a * scale, b * scale, c * scale];
}

export interface SessionWorkspaceSplitProps {
  list: React.ReactNode;
  /** Middle column (details / inspector). Planned to also host the session narrative alongside item description. */
  inspector: React.ReactNode;
  share: React.ReactNode;
}

/**
 * Three equal-by-default columns (list | inspector | share) with two draggable gutters.
 * The inspector column is the description panel today; narrative UI is expected to live here later too.
 */
export function SessionWorkspaceSplit({ list, inspector, share }: SessionWorkspaceSplitProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ which: 1 | 2; startX: number; w1: number; w2: number; w3: number } | null>(null);
  const weightsRef = useRef<[number, number, number]>(DEFAULT);
  const [w, setW] = useState<[number, number, number]>(() => readStored() ?? DEFAULT);
  weightsRef.current = w;

  const normalize = useCallback((a: number, b: number, c: number) => clampWeights(a, b, c), []);

  useLayoutEffect(() => {
    setW(([a, b, c]) => normalize(a, b, c));
  }, [normalize]);

  useEffect(() => {
    const onResize = () => setW(([a, b, c]) => normalize(a, b, c));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [normalize]);

  const onDown = (which: 1 | 2) => (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const [a, b, c] = weightsRef.current;
    dragRef.current = { which, startX: e.clientX, w1: a, w2: b, w3: c };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    const el = rootRef.current;
    if (!d || !el) return;
    const rect = el.getBoundingClientRect();
    const total = rect.width - 12; // two 6px handles
    if (total <= 0) return;
    const deltaW = ((e.clientX - d.startX) / total) * SUM;

    if (d.which === 1) {
      const n3 = d.w3;
      let n1 = d.w1 + deltaW;
      let n2 = d.w2 - deltaW;
      n1 = Math.max(MIN, Math.min(SUM - MIN - n3, n1));
      n2 = SUM - n1 - n3;
      if (n2 < MIN) {
        const n2c = MIN;
        const n1c = SUM - n2c - n3;
        setW(normalize(n1c, n2c, n3));
      } else {
        setW(normalize(n1, n2, n3));
      }
    } else {
      const n1 = d.w1;
      let n2 = d.w2 + deltaW;
      let n3 = d.w3 - deltaW;
      n2 = Math.max(MIN, Math.min(SUM - MIN - n1, n2));
      n3 = SUM - n1 - n2;
      if (n3 < MIN) {
        const n3c = MIN;
        const n2c = SUM - n1 - n3c;
        setW(normalize(n1, n2c, n3c));
      } else {
        setW(normalize(n1, n2, n3));
      }
    }
  };

  const onUp = (e: React.PointerEvent) => {
    if (dragRef.current == null) return;
    dragRef.current = null;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(weightsRef.current));
    } catch {
      /* */
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* */
    }
  };

  const reset = useCallback(() => {
    const d = DEFAULT;
    weightsRef.current = d;
    setW(d);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* */
    }
  }, []);

  const [a, b, c] = w;

  return (
    <div
      ref={rootRef}
      className="grid min-h-0 min-w-0 flex-1 overflow-hidden"
      style={{ gridTemplateColumns: `minmax(0,${a}fr) 6px minmax(0,${b}fr) 6px minmax(0,${c}fr)` }}
    >
      <div className="min-h-0 min-w-0 overflow-hidden border-r border-[var(--border-subtle)] bg-[var(--bg-sidebar)]">{list}</div>

      <button
        type="button"
        role="separator"
        aria-orientation="vertical"
        title="Resize list vs details · double-click any handle to reset columns"
        onPointerDown={onDown(1)}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onDoubleClick={reset}
        className="group flex w-full cursor-col-resize touch-none flex-col items-center justify-center border-x border-transparent bg-[var(--bg-editor)] outline-none hover:border-[var(--border-subtle)] hover:bg-[var(--bg-sidebar)] focus-visible:ring-2 focus-visible:ring-[var(--accent)]/50"
      >
        <span className="h-10 w-1 rounded-full bg-[var(--border)] opacity-70 group-hover:opacity-100" />
      </button>

      <div className="min-h-0 min-w-0 overflow-hidden bg-[var(--bg-panel)]">{inspector}</div>

      <button
        type="button"
        role="separator"
        aria-orientation="vertical"
        title="Resize details vs sharing · double-click to reset"
        onPointerDown={onDown(2)}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onDoubleClick={reset}
        className="group flex w-full cursor-col-resize touch-none flex-col items-center justify-center border-x border-transparent bg-[var(--bg-editor)] outline-none hover:border-[var(--border-subtle)] hover:bg-[var(--bg-sidebar)] focus-visible:ring-2 focus-visible:ring-[var(--accent)]/50"
      >
        <span className="h-10 w-1 rounded-full bg-[var(--border)] opacity-70 group-hover:opacity-100" />
      </button>

      <div className="min-h-0 min-w-0 overflow-hidden border-l border-[var(--border-subtle)] bg-[var(--bg-sidebar)]">{share}</div>
    </div>
  );
}
